import type { DeploymentType } from '@/components/modals/deploymentOptions'
import { DEFAULT_ATTRIBUTE_ORDER } from '@/components/products/productColumns'
import type {
  CreateConfigurationRequest,
  GenerateRulesResponse,
} from '@/services/chatBuilder'
import type {
  BuilderDesignConfig,
  BuilderRulesConfig,
} from '@/stores/chatBuilderStore'

interface BuildArgs {
  generated: GenerateRulesResponse['data'] | undefined
  instructions: string
  deploymentType: DeploymentType
  collectionId?: string
  collectionName?: string
}

const DEFAULT_RULES: BuilderRulesConfig = {
  pageViewCapture: { enabled: false },
  recommendations: { rules: [] },
  conversation: { rules: [] },
  toneOfVoice: { description: 'Professional & courteous' },
}

// Mirrors the inline merge previously in
// DashboardPage.handleConfirmCreateAgent. Extracted so the onboarding
// flow's Brand & Goal step can reuse the exact same configuration
// shape as the Create Agent modal.
export function buildConfigurationFromGeneratedRules(
  args: BuildArgs
): CreateConfigurationRequest {
  const { generated, instructions, deploymentType } = args
  const collectionId = args.collectionId ?? ''
  const collectionName = args.collectionName ?? ''

  const rulesConfig = generated?.rulesConfig ?? DEFAULT_RULES
  const agentName = generated?.agentName ?? 'New Agent'
  const brandColors = generated?.designConfig?.colors
  const brandTypography = generated?.designConfig?.typography
  const brandTheme = generated?.designConfig?.styles?.theme
  const brandTitleBarStyle = generated?.designConfig?.logo?.titleBarStyle
  const brandLogoUrl = generated?.logoUrl

  const designConfig: BuilderDesignConfig = {
    colors: {
      primary: brandColors?.primary ?? '#007bff',
      background: brandColors?.background ?? '#ffffff',
      accent: brandColors?.bubbleColor ?? brandColors?.primary,
      primaryText: brandColors?.primaryText ?? '#ffffff',
      systemText: brandColors?.systemText ?? '#000000',
      cardText: brandColors?.cardText ?? '#000000',
      bubbleColor: brandColors?.bubbleColor ?? '#F0F0F0',
      primaryPalette: brandColors?.primaryPalette,
      accentPalette: brandColors?.accentPalette,
      backgroundPalette: brandColors?.backgroundPalette,
      primaryTextPalette: brandColors?.primaryTextPalette,
      systemTextPalette: brandColors?.systemTextPalette,
      cardTextPalette: brandColors?.cardTextPalette,
    },
    typography: {
      fontFamily: brandTypography?.fontFamily ?? 'Arial',
      fontSize: brandTypography?.fontSize ?? 16,
      lineHeight: brandTypography?.lineHeight ?? 'auto',
    },
    icons: {
      avatarSize: 'medium',
      aiMessengerFile: undefined,
      sendButtonFile: undefined,
    },
    styles: {
      responseStyle: 'bubble',
      // Theme is inferred server-side from the brand's styleguide (radius,
      // shadows, mode, fonts). Fall back to classic only when the scan
      // surfaced no brand data.
      theme: brandTheme ?? 'classic',
    },
    productTiles: {
      size: 'medium',
      style: 'cards',
      carouselConfig: {
        autoScroll: false,
        scrollSpeed: 5,
        showIndicators: true,
        itemsPerView: 2,
      },
    },
    text: {
      loadingText: 'Thinking...',
      inputPlaceholder: 'Ask anything...',
    },
    disclaimer: {
      enabled: false,
      text: '',
    },
    logo: {
      titleText: '',
      alignment: 'center',
      showLogo: true,
      titleBarSize: 'medium',
      titleBarStyle: brandTitleBarStyle ?? 'solid',
      opacity: 100,
      fit: 'contain',
    },
    avatar: {
      opacity: 100,
      fit: 'contain',
    },
    sendButton: {
      opacity: 100,
      fit: 'contain',
    },
    background: {
      opacity: 100,
      fit: 'cover',
    },
  }

  return {
    name: agentName,
    description: instructions,
    deploymentType,
    rulesConfig,
    productConfig: {
      catalog: { collectionId, collectionName },
      display: {
        visibleAttributes: ['image', 'title', 'price'],
        attributeOrder: DEFAULT_ATTRIBUTE_ORDER,
        productsToDisplay: 3,
        showReferencedProducts: false,
        lineLimit: 'none',
      },
      welcomeProducts: { enabled: false, count: 0, productIds: [] },
      fallbackProducts: { enabled: false, count: 0, productIds: [] },
      addToCart: { enabled: false },
    },
    designConfig,
    brandLogoUrl,
  }
}
